// The workspace the app shows: the person's own account, or a team they belong to.
// Kept per browser and only for the uid that chose it. Every change of workspace goes
// through a full reload: each view loads its own data in created() and there is no
// central boot step, so a reload is what guarantees no view keeps the previous one's data.

const SELECTION_KEY = "selected-workspace";
const UNAVAILABLE_KEY = "team-unavailable";

export const ROLE_LABELS = {
  admin: "Admin",
  member: "Member",
  viewer: "Viewer",
};

// set right before every reload of the page: from then on the stored selection is the next
// page's, so nothing may be sent under it by the page that is about to unload
let reloading = false;

export function isReloading() {
  return reloading;
}

function reload() {
  reloading = true;
  window.location.reload();
}

export function getSelectedTeamId(uid) {
  if (!uid) return null;
  try {
    const saved = JSON.parse(localStorage.getItem(SELECTION_KEY) || "null");
    return saved && saved.uid === uid && typeof saved.teamId === "string"
      ? saved.teamId
      : null;
  } catch {
    return null;
  }
}

/**
 * teamId null forgets the choice; the person's own uid selects their own account.
 * Returns whether the choice is stored (the key is gone, for null). A refused write leaves
 * what was stored as it was, so the screen and the X-Team-Id keep agreeing
 */
export function setSelectedTeamId(uid, teamId) {
  try {
    if (teamId === null) {
      localStorage.removeItem(SELECTION_KEY);
      return localStorage.getItem(SELECTION_KEY) === null;
    }
    const value = JSON.stringify({ uid, teamId });
    localStorage.setItem(SELECTION_KEY, value);
    return localStorage.getItem(SELECTION_KEY) === value;
  } catch {
    // no storage (private mode, blocked, full): the app shows what it showed
    return false;
  }
}

/** the X-Team-Id to send: never for the person's own account */
export function teamHeaderFor(uid) {
  const teamId = getSelectedTeamId(uid);
  return teamId && teamId !== uid ? teamId : null;
}

/**
 * replaces the current history entry rather than adding one (AGENTS.md: a one-time step
 * leaves with replace): back must not reopen a spent invitation or the previous workspace.
 * Does nothing when the choice cannot be stored: the reload would not open it, and the
 * first-load rule would switch again on every load
 */
export function switchWorkspace(uid, teamId) {
  if (!setSelectedTeamId(uid, teamId)) return;
  window.history.replaceState(null, "", "#/");
  reload();
}

/**
 * the API no longer knows the person in the selected team: their own account is selected, not
 * cleared, so that a team joined last is not opened instead; the next load shows the notice
 */
export function leaveUnavailableTeam(uid) {
  if (!setSelectedTeamId(uid, uid)) {
    // the team left would stay stored: its header goes out again, another 403, another reload
    try {
      localStorage.removeItem(SELECTION_KEY);
    } catch {
      // storage refuses everything: nothing more to do
    }
  }
  try {
    sessionStorage.setItem(UNAVAILABLE_KEY, "1");
  } catch {
    // the notice is skipped, the fallback still happens
  }
  reload();
}

/** true once after leaveUnavailableTeam(), for the notice App.vue shows */
export function takeTeamUnavailableNotice() {
  try {
    const shown = sessionStorage.getItem(UNAVAILABLE_KEY) === "1";
    sessionStorage.removeItem(UNAVAILABLE_KEY);
    return shown;
  } catch {
    return false;
  }
}

/**
 * another tab changed the workspace (switch, logout, removal): reload, so this tab never keeps
 * showing one workspace while its next call goes to another. The event fires only in the
 * other tabs, not in the one that wrote
 */
export function followOtherTabs() {
  window.addEventListener("storage", (event) => {
    if (event.key === SELECTION_KEY || event.key === null) {
      reload();
    }
  });
}
